"use client";

import { type PropsWithChildren, useEffect } from "react";
import {
    FEATURE_FLAGS_METADATA,
    type FeatureFlagMetadata,
} from "./metadata";

type FeatureFlagValue = boolean | string;

type FeatureFlagsMetadataBridgeProps = PropsWithChildren<{
    enabled: boolean;
}>;

export type FeatureFlagsMetadataBridgeValue = {
    version: 1;
    flags: Readonly<Record<string, FeatureFlagMetadata>>;
};

export const FEATURE_FLAGS_METADATA_BRIDGE_VALUE = {
    version: 1,
    flags: FEATURE_FLAGS_METADATA,
} as const satisfies FeatureFlagsMetadataBridgeValue;

declare global {
    interface Window {
        __FEATURE_FLAGS_METADATA__?: FeatureFlagsMetadataBridgeValue;
        featureFlags?: Record<string, FeatureFlagValue>;
    }
}

const decodeOverrides = (encodedPayload: string): unknown => {
    const base64 = encodedPayload.replace(/-/g, "+").replace(/_/g, "/");
    const padding = "=".repeat((4 - (base64.length % 4)) % 4);
    const binary = window.atob(base64 + padding);
    const percentEncoded = Array.from(
        binary,
        (character) =>
            `%${character.charCodeAt(0).toString(16).padStart(2, "0")}`
    ).join("");

    return JSON.parse(decodeURIComponent(percentEncoded));
};

const getValidOverrides = (
    payload: unknown
): Record<string, FeatureFlagValue> => {
    if (
        typeof payload !== "object" ||
        payload === null ||
        Array.isArray(payload) ||
        !("v" in payload) ||
        payload.v !== 1 ||
        !("flags" in payload) ||
        typeof payload.flags !== "object" ||
        payload.flags === null ||
        Array.isArray(payload.flags)
    ) {
        return {};
    }

    const metadataByFlag: Readonly<
        Record<string, FeatureFlagMetadata | undefined>
    > = FEATURE_FLAGS_METADATA;

    return Object.entries(payload.flags).reduce<
        Record<string, FeatureFlagValue>
    >((validOverrides, [flag, value]) => {
        const metadata = metadataByFlag[flag];

        if (
            (metadata?.type === "boolean" && typeof value === "boolean") ||
            (metadata?.type === "variant" &&
                typeof value === "string" &&
                metadata.variants.includes(value))
        ) {
            validOverrides[flag] = value;
        }

        return validOverrides;
    }, {});
};

const FeatureFlagsMetadataBridge = ({
    children,
    enabled,
}: FeatureFlagsMetadataBridgeProps) => {
    useEffect(() => {
        if (!enabled) {
            return;
        }

        window.__FEATURE_FLAGS_METADATA__ =
            FEATURE_FLAGS_METADATA_BRIDGE_VALUE;

        const encodedPayload = new URLSearchParams(window.location.search).get(
            "__fp"
        );

        if (!encodedPayload) {
            return;
        }

        try {
            const validOverrides = getValidOverrides(
                decodeOverrides(encodedPayload)
            );

            if (Object.keys(validOverrides).length === 0) {
                return;
            }

            window.featureFlags = {
                ...window.featureFlags,
                ...validOverrides,
            };
            window.dispatchEvent(
                new CustomEvent("optimizely/campaign/decided")
            );
        } catch {
            // Invalid URL payloads must not affect the page.
        }
    }, [enabled]);

    return children;
};

export default FeatureFlagsMetadataBridge;
